/*************************************************************************
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

import UAParser from "ua-parser-js";

export const INITIAL_DPI = window.devicePixelRatio;

export const deviceTypes = {
  MOBILE: "mobile",
  TABLET: "tablet"
};

export const getDeviceInfo = () => {
  const parser = new UAParser();
  return parser.getDevice();
};

export const getOS = () => {
  const parser = new UAParser();
  return parser.getOS();
};

export const isCurrentDeviceMobile = () => {
  const deviceInfo = getDeviceInfo();
  return Boolean(
    deviceInfo.type && deviceInfo.type.toLowerCase() === deviceTypes.MOBILE
  );
};

export const isCurrentDeviceTouch = () =>
  navigator.maxTouchPoints > 0 ||
  (!!navigator.platform && /iPad|iPhone/.test(navigator.platform));

export const isMobileDevice = () =>
  isCurrentDeviceTouch() && window.orientation !== undefined;

export const isCurrentDeviceTablet = () => {
  const deviceInfo = getDeviceInfo();
  return Boolean(deviceInfo.type && deviceInfo.type.match(/tablet/i) !== null);
};

export const isTouchDevice = (global = window) =>
  "ontouchstart" in global ||
  !!(
    global.navigator &&
    (global.navigator.msMaxTouchPoints || global.navigator.maxTouchPoints)
  ) ||
  isCurrentDeviceMobile() ||
  isCurrentDeviceTablet();

export const getPhysicalDevicePixelRatio = (global = window) =>
  global.devicePixelRatio;

export const getDevicePixelRatio = (global = window) =>
  getPhysicalDevicePixelRatio(global);

export const getDimensionAlignedToDevicePixels = (
  dimensionParam,
  global = window
) => {
  const devicePixelRatio = getDevicePixelRatio(global);
  const dimension = Math.ceil(dimensionParam * devicePixelRatio);
  let alignToPixelCount = 1;
  const epsilon = 0.01;

  if (Math.abs(devicePixelRatio - 1.5) < epsilon) alignToPixelCount = 3;
  // handle devicePixelRatio = 1.5 - cutting of edge pixels
  else if (Math.abs(devicePixelRatio - 2) < epsilon) alignToPixelCount = 2;
  // handle devicePixelRatio = 2
  else if (Math.abs(devicePixelRatio - 3) < epsilon) alignToPixelCount = 3;
  // handle devicePixelRatio = 3
  else if (Math.abs(devicePixelRatio - 4) < epsilon) alignToPixelCount = 4;
  // handle devicePixelRatio = 4
  else if (Math.abs(devicePixelRatio - 1.25) < epsilon) alignToPixelCount = 5;

  const alignedDimension = dimension - (dimension % alignToPixelCount);

  const delta = 0.2;
  return Math.floor(alignedDimension / devicePixelRatio + delta);
};

export const isIOS = () => {
  const os = getOS();
  return os && os.name === "iOS";
};

export const isMac = () => {
  const os = getOS();
  return os && os.name === "Mac OS";
};

export const isIpad = () => {
  const os = getOS();
  return isCurrentDeviceTablet() && os && os.name === "iOS";
};

export const isWindows = () => {
  const os = getOS();
  return os && os.name === "Windows";
};
